/**
 * Eligibility und Suche fuer den Radar-Chart-Vergleich auf der Talent-Detailseite
 * und fuer die Talentauswahl der Teamanalyse.
 *
 * Reine Logik, absichtlich ohne React-Import, damit sie unter `yarn test`
 * laufen kann. Komponenten in diesem Baum ziehen ueber Umwege react-dnd,
 * daran stirbt jest.
 */

const byUpdatedAtDesc = (a, b) =>
  Date.parse(b?.updatedAt || 0) - Date.parse(a?.updatedAt || 0)

/**
 * Liegt ein Talent ausserhalb des Talentspeichers? Zaehlt sowohl die Maske der
 * Talentliste (storage_locked, nur in enforced gesetzt) als auch isLocked aus
 * useTalentStorage. Beides zu pruefen ist Absicht, denn Radar und Teamanalyse
 * laden echte Ergebnisse per owner nach und duerfen sich nicht allein auf die
 * Maske verlassen. Ohne isLocked zaehlt nur die Maske.
 *
 * @param {object} space
 * @param {(id: string) => boolean} [isLocked]
 */
export const isStorageLockedSpace = (space, isLocked) =>
  !!space &&
  (space.storage_locked === true ||
    (typeof isLocked === 'function' && !!isLocked(space.id)))

/**
 * Teilt die Talente des Partners in vergleichbar und nicht vergleichbar.
 *
 * Die Regel steht bewusst nur hier:
 * - das aktuell geoeffnete Talent ist nicht vergleichbar
 * - vergleichbar ist jedes Talent mit abgeschlossenem Assessment und owner
 * - der context (TALENTPOOL, SELECT, ATTRACT) spielt KEINE Rolle
 *
 * Der frueher an dieser Stelle stehende Filter `context === 'TALENTPOOL'` war
 * die Ursache dafuer, dass Kunden ihre Stellen-Bewerbenden nicht vergleichen
 * konnten. useTalentsQuery entfernt EMPLOYEE, soft_deleted und ATTRACT ohne
 * mail_stored bereits vorher, eine weitere Einschraenkung braucht es nicht.
 *
 * `owner` gehoert in die Eligibility, weil die Antworten des Talents ueber
 * `listAnswersWithAivyOutput` am owner haengen. Ohne owner laeuft der Vergleich
 * in einen Fehler statt in ein Diagramm.
 */
export const buildCompareCandidates = ({ spaces, originSpaceId, isLocked }) => {
  const others = (spaces || []).filter(({ id }) => id !== originSpaceId)

  const completed = others.filter(
    ({ progress, owner }) => progress === 100 && !!owner
  )

  // Gesperrte Talente (Talentspeicher) waeren vergleichbar, ihre Antworten
  // bleiben aber zu. Eigener Bucket, damit die UI die Zahl nennen kann.
  // Gesperrte ohne abgeschlossenes Assessment bleiben in incomplete, dort
  // steht nur ihr Status, und der ist auch gesperrt sichtbar.
  const eligible = completed
    .filter((space) => !isStorageLockedSpace(space, isLocked))
    .sort(byUpdatedAtDesc)

  const locked = completed
    .filter((space) => isStorageLockedSpace(space, isLocked))
    .sort(byUpdatedAtDesc)

  // Getrennt gezaehlt statt totalCount - eligibleCount, sonst wuerde ein Space
  // ohne owner in der Copy als "Assessment nicht abgeschlossen" auftauchen.
  const incomplete = others
    .filter(({ progress }) => progress !== 100)
    .sort(byUpdatedAtDesc)

  return {
    eligible,
    incomplete,
    locked,
    eligibleCount: eligible.length,
    incompleteCount: incomplete.length,
    lockedCount: locked.length,
    totalCount: others.length
  }
}

/**
 * Talentauswahl der Teamanalyse. Auswaehlbar sind Talente mit abgeschlossenem
 * Assessment, die nicht gesperrt sind. lockedCount nennt die abgeschlossenen
 * Gesperrten fuer den Hinweis. Bestehende Teamanalysen beruehrt das nicht,
 * es betrifft nur neue Auswahlen.
 *
 * @param {{ spaces?: object[], isLocked?: (id: string) => boolean }} params
 * @returns {{ selectable: object[], lockedCount: number }}
 */
export const buildTeamAnalyseCandidates = ({ spaces, isLocked }) => {
  const completed = (spaces || []).filter(({ progress }) => progress === 100)
  const selectable = completed.filter(
    (space) => !isStorageLockedSpace(space, isLocked)
  )

  return {
    selectable,
    lockedCount: completed.length - selectable.length
  }
}

/**
 * Durchsuchbarer Text eines Talents. Nur vorhandene Felder, damit der Heuhaufen
 * nie den String "undefined" enthaelt. Genau das war der Fehler im bisherigen
 * Template-String `${space.id}/${space.name}/${space.identification}`.
 */
export const compareCandidateHaystack = (space) =>
  [
    space?.name,
    space?.identification,
    space?.email,
    space?.partner_user_id,
    space?.id
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

/**
 * Token-Suche, damit "bauer soeren" auch "Soeren Bauer" findet. Jedes Token
 * muss vorkommen, die Reihenfolge ist frei.
 */
export const filterCompareCandidates = ({ candidates, searchQuery }) => {
  const tokens = (searchQuery || '')
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter(Boolean)

  if (!tokens.length) return candidates || []

  return (candidates || []).filter((space) => {
    const haystack = compareCandidateHaystack(space)

    return tokens.every((token) => haystack.includes(token))
  })
}
